import type { Theme } from "@mui/material";

const PALETTE_SIZE = 10;

/**
 * Deterministic name → palette index (djb2 hash, mod 10). Pure and theme-free
 * so it can be unit-tested. Same name always maps to the same index.
 */
export function getAvatarColorIndex(name: string): number {
  if (!name) return 0;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % PALETTE_SIZE;
}

/**
 * Resolves the fallback background + initials color for a name from the fixed
 * 10-color palette. Every token is optional in the theme types, so each falls
 * back to `neutral.main` to keep the result `string` (never `undefined`).
 */
export function getAvatarFallbackColors(
  name: string,
  theme: Theme,
): { backgroundColor: string; textColor: string } {
  const { system } = theme.palette.primitive;
  const fb = theme.palette.neutral.main;
  const palette: Array<{ backgroundColor: string; textColor: string }> = [
    { backgroundColor: system.teal[200] ?? fb, textColor: system.teal[900] ?? fb },
    { backgroundColor: system.yellow[100] ?? fb, textColor: system.yellow[900] ?? fb },
    { backgroundColor: system.amber[200] ?? fb, textColor: system.amber[900] ?? fb },
    { backgroundColor: system.red[200] ?? fb, textColor: system.red[900] ?? fb },
    { backgroundColor: system.blue[200] ?? fb, textColor: system.blue[900] ?? fb },
    { backgroundColor: system.indigo[200] ?? fb, textColor: system.indigo[900] ?? fb },
    { backgroundColor: system.orange[50] ?? fb, textColor: system.orange[900] ?? fb },
    { backgroundColor: system.green[100] ?? fb, textColor: system.green[900] ?? fb },
    {
      backgroundColor: theme.palette.secondary["purple-tint-3"] ?? fb,
      textColor: system.violet[900] ?? fb,
    },
    { backgroundColor: system.magenta[100] ?? fb, textColor: system.magenta[900] ?? fb },
  ];
  return palette[getAvatarColorIndex(name)];
}
